import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  Menu,
  X,
  ArrowRight,
  Clock,
  CheckCircle,
  Sparkles,
  Users,
  BarChart3,
  Moon,
  Clock3,
  FileEdit,
  Zap,
  MessageCircle,
  Wand2,
  FileText,
  ClipboardList,
  ShieldCheck,
  Timer,
  Image,
  MessageSquareText,
  Calculator,
  BookOpen,
  FlaskConical,
  Globe,
  Landmark,
  Terminal,
  Languages,
  Palette,
  Star,
} from 'lucide-angular';
import { AmplitudeService } from '../../common/analytics/amplitude.service';
import { MetadataApiService } from '../../common/services/metadata-api.service';
import {
  Grade,
  Tier1Subject,
} from '../../common/services/metadata.models';

@Component({
  selector: 'app-assignment-landing',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './assignment-landing.html',
  styleUrl: './assignment-landing.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentLandingComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly metadataApiService = inject(MetadataApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly isMenuOpen = signal(false);

  // Metadata from API
  readonly grades = signal<Grade[]>([]);
  readonly tier1Subjects = signal<Tier1Subject[]>([]);

  // Dropdown state (using real data types)
  readonly selectedGrade = signal<number | null>(null);
  readonly selectedTier1SubjectId = signal<string | null>(null);

  ngOnInit(): void {
    this.amplitudeService.trackEvent('assignment_landing_page_viewed');
    this.loadMetadata();
  }

  readonly icons = {
    Menu,
    X,
    ArrowRight,
    Clock,
    CheckCircle,
    Sparkles,
    Users,
    BarChart3,
    Moon,
    Clock3,
    FileEdit,
    Zap,
    MessageCircle,
    Wand2,
    FileText,
    ClipboardList,
    ShieldCheck,
    Timer,
    Image,
    MessageSquareText,
    Calculator,
    BookOpen,
    FlaskConical,
    Globe,
    Landmark,
    Terminal,
    Languages,
    Palette,
    Star,
  };

  toggleMenu(): void {
    this.isMenuOpen.update((v) => !v);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  scrollToSection(event: Event, sectionId: string): void {
    event.preventDefault();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  goToAssignment(ctaSource: string): void {
    this.amplitudeService.trackEvent('assignment_landing_cta_clicked', {
      cta_source: ctaSource,
    });
    this.router.navigate(['/assignment']);
  }

  onGenerate(): void {
    const grade = this.selectedGrade();
    const tier1SubjectId = this.selectedTier1SubjectId();

    // Resolve labels for analytics
    const gradeLabel =
      this.grades().find((g) => g.value === grade)?.label ?? null;
    const subjectLabel =
      this.tier1Subjects().find((s) => s.id === tier1SubjectId)?.category ??
      null;

    this.amplitudeService.trackEvent('assignment_landing_cta_clicked', {
      cta_source: 'generate_btn',
      grade: gradeLabel,
      subject: subjectLabel,
    });

    // Build query params for pre-selection in the wizard
    const queryParams: Record<string, string> = {};
    if (grade !== null) {
      queryParams['grade'] = String(grade);
    }
    if (tier1SubjectId) {
      queryParams['tier1SubjectId'] = tier1SubjectId;
    }

    this.router.navigate(['/assignment'], { queryParams });
  }

  private loadMetadata(): void {
    this.metadataApiService
      .getMetadata()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((metadata) => {
        this.grades.set(metadata.grades);
        this.tier1Subjects.set(metadata.tier1Subjects);
      });
  }
}
